import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { UtilityService } from '@eforall/common';
import { Volunteer, VOLUNTEER_INTERVIEW_RATING_VALUE_MAP, VolunteeringData, VolunteerProspect, VolunteerSignupSpecialist, VolunteerStatusId } from '@interfaces';
import { AppService, FuncService, TrimesterService } from '../../services';


@Injectable({ providedIn: 'root' })
export class VolunteeringAreaService {

	private func = inject(FuncService);
	private util = inject(UtilityService);
	private trimesterService = inject(TrimesterService);
	public volunteeringData = signal<VolunteeringData | undefined>(undefined);
	public app = inject(AppService);
	public specialistSignUpsCountMapByTrimester: Record<number, { currentTrimester: number, nextTrimester: number }> = {};

	constructor() {
		toObservable(this.trimesterService.selectedTrimester).subscribe(async selectedTrimester => {
			if (selectedTrimester)
				await this.loadVolunteeringData();
		})
	}

	public staffPermissions = computed(() => this.app.data().permissions);

	public canManageVolunteerStaff = computed(() => this.app.data().permissions.canManageVolunteerStaff);

	public selectedTrimesterName = computed(() => this.app.getTrimesterNameById(this.volunteeringData()?.selectedTrimester?.trimesterId));

	public selectedNextTrimesterName = computed(() => this.app.getTrimesterNameById(this.volunteeringData()?.selectedNextTrimester?.trimesterId));

	private async loadVolunteeringData() {
		const trimesterId = this.trimesterService.selectedTrimester()?.trimesterId;

		if (trimesterId) {
			const volunteerStatus = [
				VolunteerStatusId.ApprovedWithCaution,
				VolunteerStatusId.Approved,
				VolunteerStatusId.RemovedDueToInactivity,
				VolunteerStatusId.RemovedDueToCause,
				VolunteerStatusId.RemovedByRequest
			];

			const selectedTrimester = this.trimesterService.selectedTrimester();
			if (!selectedTrimester) return;

			const data = await this.func.volunteering.getVolunteeringData({
				volunteerStatus,
				trimesterId: selectedTrimester.trimesterId
			});
			this.getSpecialistSignUpsCountMapByTrimester(data.signUpSpecialists, data.selectedTrimester.trimesterId, data.selectedNextTrimester?.trimesterId);

			this.volunteeringData.set({
				...data,
				volunteerProspects: data.volunteerProspects.map(volunteer => ({
					...volunteer,
					interviewScore: this.getInterviewScore(volunteer),
				})),
				volunteers: data.volunteers.map(volunteer => ({
					...volunteer,
					interviewScore: this.getInterviewScore(volunteer),
				})),
			});
		}

	}

	private getInterviewScore(volunteer: VolunteerProspect | Volunteer): number | undefined {
		if (!volunteer.volunteerInterview) return undefined;

		return this.util.score.calculateInterviewScore([
			volunteer.volunteerInterview.missionAlignmentRating,
			volunteer.volunteerInterview.experienceRating,
			volunteer.volunteerInterview.scheduleRating,
			volunteer.volunteerInterview.mentorshipStyleRating,
			volunteer.volunteerInterview.presentationStyleRating
		], VOLUNTEER_INTERVIEW_RATING_VALUE_MAP);
	}

	// To be called in ngOnInit of child pages; loads data only once using a flag
	async getVolunteeringData() {
		if (!this.volunteeringData()) {
			await this.loadVolunteeringData();
		}
	}

	// to reload the data
	async reloadData() {
		await this.loadVolunteeringData();
	}

	async addEngagementIdToContactRequest(contactRequestId: number, engagementId: number) {
		await this.func.contactRequest.addEngagementId({ contactRequestId, engagementId });
	}

	public async updateAssignedStaffForVolunteer(volunteerId: number, assignedToStaffId: number | undefined) {
		await this.func.volunteer.updateVolunteerAssignedStaff({ volunteerId, assignedToStaffId });
		await this.getVolunteeringData();
	}

	// Creates a map of volunteerId(of specialists) to their sign ups count for current and next trimester
	private getSpecialistSignUpsCountMapByTrimester(signUps: VolunteerSignupSpecialist[], currentTrimesterId: number, nextTrimesterId: number | undefined) {
		this.specialistSignUpsCountMapByTrimester = signUps.reduce((map, signUp) => {
			const key = signUp.volunteerId;
			if (!map[key]) {
				map[key] = { currentTrimester: 0, nextTrimester: 0 };
			}

			if (signUp.trimesterId === currentTrimesterId) {
				map[key].currentTrimester++;
			} else if (signUp.trimesterId === nextTrimesterId) {
				map[key].nextTrimester++;
			}
			return map;
		}, {} as Record<number, { currentTrimester: number, nextTrimester: number }>);
	}
}